import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
    CheckCircle2, Flag, ListChecks, Loader2, RefreshCcw,
    ArrowRight, Split, X, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

/**
 * VAULT Review Queue — the "ready to review" inbox.
 * Reads /api/transaction-review/queue and lets an operator mark each
 * transaction Reviewed / Flagged, add splits, and link to a subscription
 * or document. Foundation for VAULT Watch + VAULT Rules.
 */
const TABS = [
    { key: "unreviewed", label: "Unreviewed" },
    { key: "flagged",    label: "Flagged"    },
];

export default function ReviewQueue() {
    const [tab, setTab] = useState("unreviewed");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [selected, setSelected] = useState(() => new Set());
    const [splitOpen, setSplitOpen] = useState(null); // expense doc being split
    const [err, setErr] = useState(null);

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const r = await api.get("/transaction-review/queue?limit=300&include_flagged=true");
            setData(r.data);
            setSelected(new Set());
        } catch (e) {
            setErr(e?.response?.data?.detail || "Could not load the review queue.");
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const items = useMemo(() => {
        const all = data?.items || [];
        return all.filter((it) => {
            const s = it.review_status || "Unreviewed";
            if (tab === "unreviewed") return s === "Unreviewed";
            if (tab === "flagged") return s === "Flagged";
            return true;
        });
    }, [data, tab]);

    const toggle = (id) => {
        const next = new Set(selected);
        next.has(id) ? next.delete(id) : next.add(id);
        setSelected(next);
    };
    const toggleAll = () => {
        if (selected.size === items.length) setSelected(new Set());
        else setSelected(new Set(items.map((i) => i.id)));
    };

    const act = async (ids, status) => {
        if (!ids.length) return;
        setBusy(true);
        try {
            if (ids.length === 1) {
                await api.post(`/transaction-review/${ids[0]}/review`, { status });
            } else {
                await api.post("/transaction-review/bulk-review", { ids, status });
            }
            toast.success(`${ids.length} transaction${ids.length > 1 ? "s" : ""} marked ${status.toLowerCase()}.`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not update.");
        } finally { setBusy(false); }
    };

    return (
        <div className="max-w-[1100px] mx-auto p-6" data-testid="review-queue-page">
            {/* Header */}
            <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.24em] text-stone-500 font-semibold flex items-center gap-2">
                        <ListChecks size={13} strokeWidth={2.2}/> INBOX · TRANSACTION REVIEW
                    </div>
                    <h1 className="text-3xl font-serif font-medium text-stone-900 tracking-tight mt-2">
                        Ready to review.
                    </h1>
                    <p className="text-sm text-stone-500 mt-1.5 max-w-xl">
                        Every new transaction lands here first. Mark it reviewed, flag anything
                        unusual, or split a mixed charge across categories.
                    </p>
                </div>
                <Button
                    onClick={load}
                    variant="outline"
                    className="h-9 text-xs"
                    disabled={loading}
                    data-testid="review-refresh"
                >
                    {loading ? <Loader2 className="animate-spin mr-2" size={13}/> : <RefreshCcw size={13} className="mr-2"/>}
                    Refresh
                </Button>
            </div>

            {/* Counters */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {[
                    { label: "Unreviewed", key: "unreviewed", tone: "amber" },
                    { label: "Flagged", key: "flagged", tone: "red" },
                    { label: "Reviewed today", key: "reviewed_last_24h", tone: "emerald" },
                    { label: "Total in queue", key: "total", tone: "stone" },
                ].map((c) => (
                    <div key={c.key} className="rounded-xl bg-white border border-stone-200 p-4" data-testid={`review-count-${c.key}`}>
                        <div className="text-[10px] uppercase tracking-widest text-stone-500">{c.label}</div>
                        <div className={`text-2xl font-medium mt-1 ${
                            c.tone === "amber" ? "text-amber-700" :
                            c.tone === "red" ? "text-red-700" :
                            c.tone === "emerald" ? "text-emerald-700" : "text-stone-900"
                        }`}>
                            {data?.counts?.[c.key] ?? 0}
                        </div>
                    </div>
                ))}
            </div>

            {/* Tabs */}
            <div className="border-b border-stone-200 mb-4 flex items-center gap-1">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition ${
                            tab === t.key ? "border-stone-900 text-stone-900" : "border-transparent text-stone-500 hover:text-stone-800"
                        }`}
                        data-testid={`review-tab-${t.key}`}
                    >
                        {t.label}
                    </button>
                ))}
                <div className="flex-1" />
                {selected.size > 0 && (
                    <div className="flex items-center gap-2 pb-2">
                        <span className="text-xs text-stone-500">{selected.size} selected</span>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => act([...selected], "Flagged")}
                            disabled={busy}
                            data-testid="review-bulk-flag"
                        >
                            <Flag size={12} className="mr-1.5"/> Flag
                        </Button>
                        <Button
                            size="sm"
                            className="bg-stone-900 hover:bg-stone-800 text-white"
                            onClick={() => act([...selected], "Reviewed")}
                            disabled={busy}
                            data-testid="review-bulk-mark"
                        >
                            <CheckCircle2 size={12} className="mr-1.5"/> Mark reviewed
                        </Button>
                    </div>
                )}
            </div>

            {/* List */}
            {err && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-800 mb-4">
                    <AlertTriangle size={14} className="inline mr-1.5" />{err}
                </div>
            )}
            {loading ? (
                <div className="py-16 text-center text-stone-400 text-sm">
                    <Loader2 className="animate-spin inline mr-2" size={14}/> Loading queue…
                </div>
            ) : items.length === 0 ? (
                <div className="py-16 rounded-2xl border border-dashed border-stone-300 text-center" data-testid="review-empty">
                    <CheckCircle2 size={22} className="text-emerald-600 mx-auto" />
                    <div className="mt-2 font-medium text-stone-900">Inbox zero.</div>
                    <div className="text-xs text-stone-500 mt-1">Nothing to review right now.</div>
                    <div className="mt-6">
                        <Link to="/expenses" className="text-xs underline text-stone-600 inline-flex items-center gap-1">
                            Go to Transactions <ArrowRight size={11}/>
                        </Link>
                    </div>
                </div>
            ) : (
                <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
                    <div className="px-4 py-2.5 border-b border-stone-100 flex items-center gap-3">
                        <Checkbox
                            checked={selected.size === items.length && items.length > 0}
                            onCheckedChange={toggleAll}
                            data-testid="review-select-all"
                        />
                        <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">
                            Select all · {items.length}
                        </div>
                    </div>
                    <ul className="divide-y divide-stone-100">
                        {items.map((it) => (
                            <li key={it.id} className="px-4 py-3 flex items-center gap-3 hover:bg-stone-50" data-testid={`review-item-${it.id}`}>
                                <Checkbox
                                    checked={selected.has(it.id)}
                                    onCheckedChange={() => toggle(it.id)}
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <div className="font-medium text-stone-900 truncate">{it.merchant || "—"}</div>
                                        {it.review_status === "Flagged" && (
                                            <span className="text-[10px] font-semibold uppercase tracking-widest text-red-700 bg-red-50 px-1.5 py-0.5 rounded-full">Flagged</span>
                                        )}
                                        {it.reimbursable && (
                                            <span className="text-[10px] font-semibold uppercase tracking-widest text-violet-700 bg-violet-50 px-1.5 py-0.5 rounded-full">Reimbursable</span>
                                        )}
                                        {it.linked_subscription_id && (
                                            <span className="text-[10px] font-semibold uppercase tracking-widest text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded-full">Linked Sub</span>
                                        )}
                                        {Array.isArray(it.splits) && it.splits.length > 0 && (
                                            <span className="text-[10px] font-semibold uppercase tracking-widest text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full">{it.splits.length}-way split</span>
                                        )}
                                    </div>
                                    <div className="text-[11px] text-stone-500 mt-0.5 flex items-center gap-2 flex-wrap">
                                        <span>{fmtDate(it.date)}</span>
                                        <span>·</span>
                                        <span>{it.category || "Uncategorised"}</span>
                                        <span>·</span>
                                        <span>{it.business_personal || "Business"}</span>
                                        {it.last_4 && <><span>·</span><span>•••• {it.last_4}</span></>}
                                    </div>
                                </div>
                                <div className="text-right shrink-0">
                                    <div className="font-semibold text-stone-900">{fmtMoney(it.amount)}</div>
                                    <div className="text-[10px] text-stone-500">{it.currency || "INR"}</div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setSplitOpen(it)}
                                        title="Split this charge"
                                        data-testid={`review-split-${it.id}`}
                                    >
                                        <Split size={13}/>
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => act([it.id], "Flagged")}
                                        disabled={busy}
                                        title="Flag for follow-up"
                                        data-testid={`review-flag-${it.id}`}
                                    >
                                        <Flag size={13} className="text-red-600"/>
                                    </Button>
                                    <Button
                                        size="sm"
                                        className="bg-stone-900 hover:bg-stone-800 text-white h-8"
                                        onClick={() => act([it.id], "Reviewed")}
                                        disabled={busy}
                                        data-testid={`review-mark-${it.id}`}
                                    >
                                        <CheckCircle2 size={13} className="mr-1"/> Mark reviewed
                                    </Button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {splitOpen && (
                <SplitDialog
                    expense={splitOpen}
                    onClose={() => setSplitOpen(null)}
                    onSaved={() => { setSplitOpen(null); load(); }}
                />
            )}
        </div>
    );
}

function SplitDialog({ expense, onClose, onSaved }) {
    const total = Number(expense.amount || 0);
    const initial = (expense.splits && expense.splits.length)
        ? expense.splits.map((s) => ({ label: s.label || "", amount: Number(s.amount || 0), category: s.category || "", business_personal: s.business_personal || "" }))
        : [
            { label: "Personal share", amount: Math.round(total / 2 * 100) / 100, category: expense.category || "", business_personal: "Personal" },
            { label: "Business share", amount: total - Math.round(total / 2 * 100) / 100, category: expense.category || "", business_personal: "Business" },
          ];
    const [lines, setLines] = useState(initial);
    const [saving, setSaving] = useState(false);

    const sum = Math.round(lines.reduce((a, l) => a + Number(l.amount || 0), 0) * 100) / 100;
    const balanced = Math.abs(sum - Math.round(total * 100) / 100) < 0.01;

    const update = (i, patch) => setLines(lines.map((l, idx) => idx === i ? { ...l, ...patch } : l));
    const addLine = () => setLines([...lines, { label: "", amount: 0, category: "", business_personal: "" }]);
    const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

    const save = async () => {
        if (!balanced) { toast.error(`Splits (₹${sum}) must equal total (₹${total}).`); return; }
        setSaving(true);
        try {
            await api.post(`/transaction-review/${expense.id}/split`, { splits: lines });
            toast.success("Split saved.");
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save split.");
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" role="dialog" data-testid="split-dialog">
            <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Split charge</div>
                        <div className="font-medium text-stone-900 mt-1">{expense.merchant} · {fmtMoney(total)}</div>
                    </div>
                    <button onClick={onClose} className="text-stone-400 hover:text-stone-900" aria-label="Close"><X size={16}/></button>
                </div>
                <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                    {lines.map((l, i) => (
                        <div key={i} className="grid grid-cols-[1fr_110px_110px_28px] gap-2 items-center" data-testid={`split-line-${i}`}>
                            <Input value={l.label} onChange={(e) => update(i, { label: e.target.value })} placeholder="Label" />
                            <Input type="number" step="0.01" value={l.amount} onChange={(e) => update(i, { amount: parseFloat(e.target.value || "0") })} />
                            <select
                                className="h-9 rounded-md border border-stone-300 text-xs px-2"
                                value={l.business_personal || ""}
                                onChange={(e) => update(i, { business_personal: e.target.value })}
                            >
                                <option value="">—</option>
                                <option value="Business">Business</option>
                                <option value="Personal">Personal</option>
                            </select>
                            <button onClick={() => removeLine(i)} className="text-stone-400 hover:text-red-600" aria-label="Remove"><X size={13}/></button>
                        </div>
                    ))}
                </div>
                <div className="mt-3 flex items-center justify-between text-xs">
                    <button onClick={addLine} className="text-stone-700 underline">+ Add line</button>
                    <div className={balanced ? "text-emerald-700" : "text-red-700"} data-testid="split-sum">
                        Sum: {fmtMoney(sum)} / {fmtMoney(total)}
                    </div>
                </div>
                <div className="mt-5 flex items-center justify-end gap-2">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button className="bg-stone-900 hover:bg-stone-800 text-white" onClick={save} disabled={saving || !balanced} data-testid="split-save">
                        {saving ? "Saving…" : "Save split"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
